import { Component, effect, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { UserService } from '@services/user.service';
import { CheckboxComponent } from '@shared/elements/checkbox/checkbox.component';
import { CommonButtonComponent } from '@shared/elements/common-button/button.component';
import { DropdownComponent } from '@shared/elements/dropdown/dropdown.component';
import { ImageComponent } from '@shared/elements/image/image.component';
import { TextFieldComponent } from '@shared/elements/text-field/text-field.component';
import { saveMandatoryUserInfoToStorage } from '@shared/helpers/system.helper';
import { Membership } from '@shared/models/membership.model';
import { RegistrationData } from '@shared/models/model';
import { LocalStorageService } from '../../../services/local-storage.service';

@Component({
  selector: 'registration',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    RouterLink,
    CommonButtonComponent,
    TextFieldComponent,
    CheckboxComponent,
    ImageComponent,
    DropdownComponent,
  ],
  templateUrl: './registration.component.html',
})
export class RegistrationComponent implements OnInit {
  loggedIn: boolean = false;

  user = new FormGroup({
    firstName: new FormControl('', [Validators.maxLength(40), Validators.required]),
    lastName: new FormControl('', [Validators.maxLength(40), Validators.required]),
    contact: new FormControl('', [Validators.maxLength(40), Validators.required]),
    email: new FormControl('', [Validators.maxLength(110), Validators.required]),
    type: new FormControl('Non-Member', [Validators.required, Validators.pattern('^(Member|Non-Member)$')]),
    membership: new FormControl('', [Validators.maxLength(100)]),
    password: new FormControl('', [Validators.minLength(4), Validators.maxLength(60), Validators.required]),
    confirmPassword: new FormControl('', [Validators.minLength(4), Validators.maxLength(60), Validators.required]),
  });

  passwordType: 'text' | 'password' = 'password';

  types: string[] = ['Non-Member', 'Member'];

  showPasswords: boolean = false;

  memberships: Membership[] = [];

  mismatch: boolean = false;

  logoId?: string | number;

  constructor(
    private storage: LocalStorageService,
    private userService: UserService,
    public router: Router,
  ) {
    if (this.storage.isLoggedIn()) {
      this.router.navigate(['/admin/dashboard']);
    }

    effect(() => {
      this.loggedIn = this.storage.isLoggedIn();

      this.logoId = this.storage.logoId();
    });

    this.userService.getMemberships().subscribe((res) => {
      if (res?.data?.content) {
        this.memberships = res.data.content;
      }
    });
  }

  ngOnInit() {
    if (this.loggedIn) {
      this.router.navigate(['/admin/dashboard']);
    }
  }

  getControl(controlName: string): FormControl {
    return this.user.get(controlName) as FormControl;
  }

  toggleType(): void {
    this.passwordType = this.passwordType === 'password' ? 'text' : 'password';
  }

  getValue(controlName: string) {
    return this.getControl(controlName).value;
  }

  getErrorMessage(controlName: string, showError: boolean = false): string {
    const control = this.getControl(controlName);
    if (control.touched || showError) {
      if (control.hasError('required')) {
        return `This field is required.`;
      } else if (control.hasError('pattern')) {
        return `Invalid ${controlName} value.`;
      } else if (control.hasError('maxlength')) {
        return `${controlName} exceeds max length.`;
      }

      if (controlName === 'confirmPassword' && control.value !== this.getValue('password')) {
        this.mismatch = true;
        return 'Passwords must match';
      } else {
        this.mismatch = false;
      }
    }

    return '';
  }

  submit(): void {
    const userData = this.user.value;

    this.userService.registration(userData as RegistrationData).subscribe((res) => {
      if (res.success) {
        this.autoLogIn(userData as { email: string; password: string; membership?: number | string; type: string });
      }
    });
  }

  autoLogIn(data: { email: string; password: string; membership?: number | string; type: string }): void {
    const loginData = { email: data['email'], password: data['password'] };
    this.userService.login(loginData).subscribe((res) => {
      const info = res.data;

      if (!info || !info?.jwt) {
        this.storage.changeLoggedIn(false);
        localStorage.clear();
        return;
      }

      saveMandatoryUserInfoToStorage(info);

      this.storage.userInfo.set(info.userInfo);
      this.storage.changeLoggedIn(true);

      if (data?.membership && data.type === 'Member') {
        this.router.navigate(['/purchase/membership'], { queryParams: { id: data.membership } });
      } else {
        this.router.navigate(['/home']);
      }
    });
  }
}
